import { Box } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'

type TestingNotice = { testing: boolean; text: string }

/**
 * The red band across the top while the system is being tested.
 *
 * <p>Driven by the same setting that stamps every printed page, so the two can
 * never disagree - a system whose invoices say UNDER TESTING while the screen
 * looks live is one where somebody sends a real customer a stamped bill.
 * Clearing {@code DOCUMENT_WATERMARK} on the server removes both at once, with
 * no release and nothing to remember.</p>
 *
 * <p>Read without signing in, because somebody handed a link for testing should
 * be told before they type a password rather than after. Asked for once every
 * five minutes: it changes when a deployment changes it, and never otherwise.</p>
 */
export function TestingBanner() {
  const { data } = useQuery({
    queryKey: ['testing-notice'] as const,
    queryFn: () => api.get<TestingNotice>('/api/v1/public/notice'),
    staleTime: 5 * 60_000,
    // A system that cannot answer is not thereby under test; say nothing.
    retry: false,
  })

  if (!data?.testing) return null

  // Repeated so the strip reads continuously however wide the screen is, and
  // marked aria-hidden past the first so a screen reader says it once.
  const message = data.text || 'UNDER TESTING'

  return (
    <Box
      role="status"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar + 2,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        bgcolor: '#b91c1c',
        color: '#fff',
        // Small on purpose: it has to be unmissable without taking a band of
        // the screen away from the work being tested.
        fontWeight: 700,
        fontSize: 9.5,
        lineHeight: 1.4,
        letterSpacing: '.14em',
        py: 0.15,
        // Somebody who cannot take movement gets the same words, held still.
        '@media (prefers-reduced-motion: reduce)': {
          '& > div': { animation: 'none', textAlign: 'center' },
        },
        '@keyframes testingScroll': {
          from: { transform: 'translateX(0)' },
          to: { transform: 'translateX(-50%)' },
        },
      }}
    >
      <Box
        sx={{
          display: 'inline-block',
          animation: 'testingScroll 18s linear infinite',
        }}
      >
        {Array.from({ length: 12 }, (_, index) => (
          <Box
            key={index}
            component="span"
            aria-hidden={index > 0}
            sx={{ px: 3 }}
          >
            {message}
          </Box>
        ))}
      </Box>
    </Box>
  )
}
